import type { Competition, Team } from '@uefadigital/panenka';
import { PkBadge, PkChip, PkIdentifier } from '@uefadigital/panenka-react';
import clsx from 'clsx';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useState } from 'react';

import { useGetAllDomesticCompetitionsQuery } from 'api/fspApi';
import { DomesticStandingsTab } from 'components/DomesticStandingTable';
import useIsDesktop from 'hooks/useIsDesktop';
import { translateEntity } from 'utils/translateEntity';

import * as style from './style.module.css';

interface DomesticTournamentData {
  homeTeam: Team;
  awayTeam: Team;
  seasonYear: string;
  isDesktop: boolean;
}

const MALE = 'MALE';
const FEMALE = 'FEMALE';
const MEN = 'MEN';
const WOMEN = 'WOMEN';

const displayTeamChip = (team: Team, selected = false, stateUpdate: Dispatch<SetStateAction<Team>>) => {
  return (
    <PkChip selected={selected} onPkClick={() => stateUpdate(team)} className="pk-pr--s">
      <PkIdentifier className="pk-p--0">
        <span slot="prefix">
          <PkBadge alt="team" fallbackImage="club-generic-badge" size={24} src={team.logoUrl} />
        </span>
        <span slot="secondary" className="pk-font-size--m pk-line-height--sm pk-text--ui-05 pk-font-weight--bold">
          {translateEntity(team)?.displayName}
        </span>
      </PkIdentifier>
    </PkChip>
  );
};

const getTeamGender = (teamType: string): string => {
  const female = teamType.indexOf(WOMEN);

  if (female !== -1) {
    return FEMALE;
  }

  return MALE;
};

export const DomesticTournament = ({ homeTeam, awayTeam, seasonYear }: DomesticTournamentData) => {
  const isDesktop = useIsDesktop();
  const homeTeamCountry = homeTeam?.countryCode ?? '0';
  const awayTeamCountry = awayTeam?.countryCode ?? '0';
  const playingTeamsGender = homeTeam?.teamTypeDetail ?? MEN;

  const [selectedCompetition, setSelectedCompetition] = useState<Competition | undefined>();
  const [selectedTeam, setSelectedTeam] = useState<Team>(homeTeam);

  const {
    data: teamCompetitions,
    isLoading: teamCompetitionsLoading,
    isError: teamTCompetitionsError,
  } = useGetAllDomesticCompetitionsQuery({
    countryCode: selectedTeam.countryCode ?? '0',
    gender: getTeamGender(playingTeamsGender),
  });

  useEffect(() => {
    const defaultCompetition = teamCompetitions?.find((competition) => competition.type === 'LEAGUE');

    setSelectedCompetition(defaultCompetition);
  }, [teamCompetitions]);

  const teamsId = homeTeam.countryCode === awayTeam.countryCode ? [homeTeam.id, awayTeam.id] : [selectedTeam.id];

  if (teamCompetitionsLoading || teamTCompetitionsError) {
    return <div />;
  }

  return (
    <>
      <div
        className={clsx({
          'pk-d--none': homeTeam.countryCode === awayTeam.countryCode,
          'pk-mb--l': true,
        })}
      >
        {displayTeamChip(homeTeam, homeTeamCountry === selectedTeam.countryCode, setSelectedTeam)}
        {displayTeamChip(awayTeam, awayTeamCountry === selectedTeam.countryCode, setSelectedTeam)}
      </div>
      <div
        className={clsx(
          style.smTableFit,
          'pk-bg--elevation-01 pk-p--m pk-p-md--l pk-mb-md--xl',
          isDesktop && 'pk-rounded--xl pk-border',
        )}
      >
        <h3 className="pk-font-size--xl pk-line-height--sm pk-text--text-01 pk-font--base-bold">
          {translateEntity(selectedCompetition)?.name} {+seasonYear - 1}/{seasonYear}
        </h3>
        <DomesticStandingsTab competition={selectedCompetition} seasonYear={seasonYear} teamsId={teamsId} />
      </div>
    </>
  );
};
